import React from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
	CheckCircleIcon,
	ExclamationTriangleIcon,
	DocumentTextIcon,
	WrenchScrewdriverIcon,
	TicketIcon,
	PlusCircleIcon,
} from '@heroicons/react/24/outline';

import useAsync from '../../../hooks/useAsync';
import useModal from '../../../hooks/useModal';

import { getTickets } from '../../../api';
import { getContracts } from '../../../api/contracts';

import { classNames } from '../../../components/helpers/functions';
import Badge from '../../../components/Badge';

const TimelineItem = ({ isLast }) => {
	const navigate = useNavigate();
	const { setOpen: onOpenTicket } = useModal('ticketDetails');
	const { setOpen: onOpenContract } = useModal('contractDetails');

	const getIcon = () => {
		switch (event.type) {
			case 'created':
				return (
					<PlusCircleIcon className="h-5 w-5 text-green-500" aria-hidden="true" />
				);
			case 'ticket':
				return (
					<TicketIcon
						className={classNames(
							'h-5 w-5',
							event.status === 'closed' ? 'text-green-500' : 'text-orange-500'
						)}
						aria-hidden="true"
					/>
				);
			case 'contract':
				return (
					<DocumentTextIcon
						className={classNames(
							'h-5 w-5',
							event.isExpired ? 'text-gray-400' : 'text-blue-500'
						)}
						aria-hidden="true"
					/>
				);
			case 'service':
				return (
					<WrenchScrewdriverIcon
						className={classNames(
							'h-5 w-5',
							event.status === 'COMPLETED' ? 'text-green-500' : 'text-indigo-500'
						)}
						aria-hidden="true"
					/>
				);
			default:
				return (
					<CheckCircleIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
				);
		}
	};

	const getStatusBadge = () => {
		switch (event.type) {
			case 'ticket':
				return (
					<Badge
						status={event.status === 'closed' ? 'success' : 'warning'}
						isBeta
						className="text-xxs"
					>
						{event.status === 'closed' ? 'Resolved' : 'Open'}
					</Badge>
				);
			case 'contract':
				return (
					<Badge
						status={event.isExpired ? 'default' : event.active ? 'success' : 'default'}
						isBeta
						className="text-xxs"
					>
						{event.isExpired ? 'Expired' : event.active ? 'Active' : 'Inactive'}
					</Badge>
				);
			case 'service':
				return (
					<Badge
						status={
							event.status === 'COMPLETED'
								? 'success'
								: event.status === 'SCHEDULED'
								? 'info'
								: 'default'
						}
						isBeta
						className="text-xxs"
					>
						{event.status === 'COMPLETED'
							? 'Completed'
							: event.status === 'SCHEDULED'
							? 'Scheduled'
							: event.status}
					</Badge>
				);
			default:
				return null;
		}
	};

	const handleClick = () => {
		switch (event.type) {
			case 'ticket':
				onOpenTicket({ id: event.id });
				break;
			case 'contract':
				onOpenContract({ id: event.id });
				break;
			case 'service':
				onOpenEvent({ id: event.id });
				break;
			default:
				break;
		}
	};

	return (
		<li>
			<div className="relative pb-8">
				{!isLast && (
					<span
						className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
						aria-hidden="true"
					/>
				)}
				<div className="relative flex space-x-3">
					<div>
						<span className="h-8 w-8 rounded-full bg-gray-50 flex items-center justify-center ring-8 ring-white">
							{getIcon()}
						</span>
					</div>
					<div
						className={classNames(
							'flex min-w-0 flex-1 justify-between space-x-4 pt-1.5',
							event.type !== 'created' && 'cursor-pointer hover:bg-gray-50 -ml-2 pl-2 pr-2 rounded-md'
						)}
						onClick={event.type !== 'created' ? handleClick : undefined}
					>
						<div>
							<p className="text-sm text-gray-900">
								{event.title}
								{event.subtitle && (
									<span className="text-gray-500 ml-2">{event.subtitle}</span>
								)}
							</p>
							{event.description && (
								<p className="text-xs text-gray-500 mt-1">{event.description}</p>
							)}
						</div>
						<div className="whitespace-nowrap text-right text-sm text-gray-500">
							<div className="flex items-center space-x-2">
								{getStatusBadge()}
								<time dateTime={event.date}>
									{moment(event.date).format('MMM D, YYYY')}
								</time>
							</div>
						</div>
					</div>
				</div>
			</div>
		</li>
	);
};

export default ({ item, refresh }) => {
	const { value: tickets } = useAsync(getTickets, {
		defaultPayload: {
			assetId: item?.id,
		},
		silent: true,
	});

	const { value: contracts } = useAsync(getContracts, {
		defaultPayload: {
			assetId: item?.id,
		},
		silent: true,
	});

	// Build timeline items from all sources
	const buildTimelineItems = () => {
		const items = [];

		// Add asset creation
		if (item?.createdAt) {
			items.push({
				id: 'created',
				type: 'created',
				title: 'Asset created',
				subtitle: `${item?.manufacturerName || item?.manufacturer?.name} ${item?.modelName || item?.model?.name}`,
				date: item.createdAt,
				sortDate: new Date(item.createdAt),
			});
		}

		// Add tickets
		tickets?.rows?.forEach((ticket) => {
			items.push({
				id: ticket.id,
				type: 'ticket',
				title: ticket.title || 'Ticket',
				description: ticket.description,
				status: ticket.status,
				date: ticket.createdAt,
				sortDate: new Date(ticket.createdAt),
			});
		});

		// Add contracts
		contracts?.rows?.forEach((contract) => {
			const isExpired = moment().isAfter(contract?.endDate);
			items.push({
				id: contract.id,
				type: 'contract',
				title: contract.name || 'Contract',
				subtitle: contract.vendor?.companyName,
				description: `${moment(contract.startDate).format('MMM D, YYYY')} - ${moment(contract.endDate).format('MMM D, YYYY')}`,
				active: contract.active,
				isExpired,
				date: contract.startDate,
				sortDate: new Date(contract.startDate),
			});
		});

		// Sort by date descending (most recent first)
		return items.sort((a, b) => b.sortDate - a.sortDate);
	};

	const timelineItems = buildTimelineItems();

	const isEmpty = timelineItems.length === 0;

	return (
		<div className="sm:mt-4 mb-10 pb-20">
			<div className="flow-root">
				{isEmpty ? (
					<div className="text-center py-12">
						<WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
						<h3 className="mt-2 text-sm font-semibold text-gray-900">No timeline events</h3>
						<p className="mt-1 text-sm text-gray-500">
							Timeline events will appear here as you add tickets, contracts, and services.
						</p>
					</div>
				) : (
					<ul role="list" className="-mb-8">
						{timelineItems.map((event, idx) => (
							<TimelineItem
								key={`${event.type}-${event.id}`}
								event={event}
								isLast={idx === timelineItems.length - 1}
							/>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};
